import { Button, Divider, FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, Radio, RadioGroup, Switch, Typography, styled } from "@mui/material"
import GlobalTextfield from "../../GlobalComponent/GlobalTextfield/GlobalTextfield";
import Astrict from "../../GlobalComponent/Astrict/Astrict";
import globalStyles from '../../Styles/ComponentStyles/global.module.scss';
import styles from "../../Styles/ComponentStyles/SpendDownCalculator/SpendDownCalculator.module.scss"
import IconsColor from "../../Utils/IconsColor";
import CancelIcon from '@mui/icons-material/Cancel';
import { useState, useEffect, useContext } from "react";
import CustomNumberTextField from "../../GlobalComponent/CustomNumberTextField/CustomNumberTextField";
import { UserContext, UserContextPayload } from "../../Context/UserDetailContext";
import agent from "../../service/api";
import { useSnackbar } from "../../GlobalComponent/GlobalSnackbar/GlobalSnackbar";

const SpendDownCalc = () => {

    const IOSSwitch = styled((props: any) => (
        <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
    ))(({ theme }) => ({
        width: 36,
        height: 20,
        padding: 0,
        margin: "5px !important",
        '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: "2px !important",
            transitionDuration: '300ms',
            top: '0px',
            '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: '#3B78CE',
                    opacity: 1,
                    border: 0,
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.5,
                },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: '#33cf4d',
                border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
                color:
                    theme.palette.mode === 'light'
                        ? theme.palette.grey[100]
                        : theme.palette.grey[600],
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
            },

        },
        '& .MuiFormControlLabel-root': {
            marginLeft: '0px !important'
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 15,
            height: 15,
        },
        '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
                duration: 500,
            }),
        }
    }));

    const {
        setFlagLoader
    } = useContext(UserContext) as UserContextPayload

    const { showSnackbar } = useSnackbar()

    const [formData, setFormData] = useState<any>({
        user_current_age: '',
        user_retirement_age: '',
        user_tpi_contribution_data: [
            { id: crypto.randomUUID(), total_contribution: '', duration: '' }
        ],
        annual_spend: '',
        user_tpi_annually_flag: true,
        user_tpi_curr_account_balance: '',
        user_tpi_percent_fees: '',
        user_tpi_employer_match: '',
        user_tpi_rate_of_return_dr: '',
        user_tpi_rate_of_return_br: '',
        user_tpi_it_rate_dr: '',
        annual_spend_percent_flag: false
    })

    // Retirement Income Data State
    const [retirementIncomeData, setRetirementIncomeData] = useState<any>(null)
    const [riAgeMin, setRiAgeMin] = useState<number | null>(null)
    const [riAgeMax, setRiAgeMax] = useState<number | null>(null)
    const [changeYear, setChangeYear] = useState<number | null>(null)
    const [riAgeArray, setRiAgeArray] = useState<number[]>([])
    const [flagShowCalculationError, setFlagShowCalculationError] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    // Initialize retirement income data when form data changes
    useEffect(() => {
        if (retirementIncomeData?.typical_plan?.RI_Age_data) {
            const riAge = Object.keys(retirementIncomeData.typical_plan.RI_Age_data).map(Number);
            setRiAgeArray(riAge);
            setRiAgeMin(riAge[0]);
            setRiAgeMax(riAge.at(-1) ?? null);
            setChangeYear(riAge[0]);
        }
    }, [retirementIncomeData]);

    // Check for calculation errors
    useEffect(() => {
        if (changeYear == Number(riAgeArray.at(-1))) {
            setFlagShowCalculationError(true)
        } else {
            setFlagShowCalculationError(false)
        }
    }, [changeYear, riAgeArray])

    const isInputBetween = (num1: any, num2: any, value: any) => value > num1 && value < num2;

    const inputChangeYear = (e: any) => {
        const val = Number.parseInt(e.target.value);
        if (val >= Number(riAgeMin) && val <= Number(riAgeMax)) {
            setChangeYear(val)
        } else if (val >= Number(riAgeMax)) {
            setChangeYear(Number(riAgeMax))
        } else {
            setChangeYear(Number(riAgeMin))
        }
    }

    const inputBlurChangeYear = (e: any) => {
        const val = Number.parseInt(e.target.value);
        const checkInBetween = isInputBetween(Number(riAgeMin), Number(riAgeMax), val)
        if (!checkInBetween) {
            if (val >= Number(riAgeMax)) {
                setChangeYear(Number(riAgeMax))
            } else {
                setChangeYear(Number(riAgeMin))
            }
        }
    }

    const addContribution = () => {
        setFormData((prev: any) => ({
            ...prev,
            user_tpi_contribution_data: [...prev.user_tpi_contribution_data, { id: crypto.randomUUID(), total_contribution: '', duration: '' }]
        }))
    }

    const removeContribution = (index: number) => {
        setFormData((prev: any) => ({
            ...prev,
            user_tpi_contribution_data: prev.user_tpi_contribution_data.filter((_: any, i: number) => i !== index)
        }))
    }

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setFormData((prev: any) => ({
            ...prev,
            user_tpi_annually_flag: value === 'annually',
            user_tpi_monthly_flag: value === 'monthly'
        }))
    }

    const handleInputChange = (field: string, value: string) => {
        const currencyFields = ['annual_spend', 'user_tpi_curr_account_balance', 'user_tpi_employer_match']
        if (currencyFields.includes(field)) {
            value = value.replaceAll(',', '')
            if (!Number.isNaN(Number(value)) && value.trim() !== '') {
                value = Number(value).toLocaleString()
            }
        }
        setFormData((prev: any) => ({ ...prev, [field]: value }))
    }

    const handleContributionChange = (index: number, field: 'total_contribution' | 'duration', value: string) => {
        if (field === 'total_contribution') {
            value = value.replaceAll(',', '')
            if (!Number.isNaN(Number(value)) && value.trim() !== '') {
                value = Number(value).toLocaleString()
            }
        }
        setFormData((prev: any) => ({
            ...prev,
            user_tpi_contribution_data: prev.user_tpi_contribution_data.map((item: any, i: number) => i === index ? { ...item, [field]: value } : item)
        }))
    }

    const buildPayload = async () => {
        try {
            setFlagLoader(true)

            if (formData?.user_retirement_age <= formData?.user_current_age) {
                setErrorMessage('Retirement age must be greater than current age.')
                return;
            } else {
                setErrorMessage(null)
            }

            const payload = {
                user_current_age: formData.user_current_age,
                user_retirement_age: formData.user_retirement_age,
                user_tpi_contribution_data: formData.user_tpi_contribution_data.map((x: any) => ({ total_contribution: x.total_contribution, duration: x.duration })),
                annual_spend: formData.annual_spend,
                user_tpi_annually_flag: !!formData.user_tpi_annually_flag,
                user_tpi_curr_account_balance: formData.user_tpi_curr_account_balance,
                user_tpi_percent_fees: formData.user_tpi_percent_fees,
                user_tpi_employer_match: formData.user_tpi_employer_match,
                user_tpi_rate_of_return_dr: formData.user_tpi_rate_of_return_dr,
                user_tpi_rate_of_return_br: formData.user_tpi_rate_of_return_br,
                user_tpi_it_rate_dr: formData.user_tpi_it_rate_dr,
                annual_spend_percent_flag: formData.annual_spend_percent_flag
            }
            const response = await agent.spendDownCalculatorAPI.post(payload);
            if (response?.status) {
                setRetirementIncomeData(response?.data);
            } else {
                showSnackbar(response?.data?.error?.message || 'Something Went Wrong!', 'error');
            }
        } catch (error) {
            console.log('SpendDown payload', error)
        }
        finally {
            setFlagLoader(false)
        }

    }

    const formatNumber = (q: any) => {
        if (!q) return q;
        return q.toLocaleString('en-US', {
            style: 'currency',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            currency: "usd"
        })
    }

    const isFormValid = () => {
        if (!formData.user_current_age ||
            !formData.user_retirement_age ||
            !formData.annual_spend ||
            !formData.user_tpi_curr_account_balance ||
            !formData.user_tpi_percent_fees ||
            !formData.user_tpi_employer_match ||
            !formData.user_tpi_rate_of_return_dr ||
            !formData.user_tpi_rate_of_return_br ||
            !formData.user_tpi_it_rate_dr) {
            return false;
        }

        const allContributionsValid = formData.user_tpi_contribution_data.every(
            (row: any) => row.total_contribution && row.duration
        );

        return allContributionsValid;
    };

    return (
        <Grid container>
            <Grid item xs={12} sx={{ padding: { xs: "10px", sm: "15px", md: "20px" } }}>
                <Grid container gap={2} className={`${styles.spendDownCalcMainWrapper} ${styles.spendDownCalcMainWrapperHeight}`}>
                    {/* Left Side Section (Input Wrapper) */}
                    <Grid item xs={12} sm={12} md={12} lg={5.6} xl={5.8} className={styles.sideScrollPane}>
                        <Grid container gap={"15px"}>
                            <Grid item xs={12}>
                                <Typography className={styles.calculatorInputsTitle}>
                                    Calculator Inputs
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={12} md={5.2} lg={6} xl={5} className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                <Grid container>
                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                            Age at Retirement <Astrict />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GlobalTextfield
                                            variant="outlined"
                                            placeholder='Enter Age at Retirement'
                                            size="medium"
                                            fullWidth
                                            autoComplete='off'
                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                            onKeyPress={(e: any) => {
                                                if (!/\d$/.test(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            onChange={(e) => handleInputChange('user_retirement_age', e.target.value)}
                                            value={formData.user_retirement_age}
                                        />
                                        {errorMessage && <label className={globalStyles.helperTextStyle}>{errorMessage}</label>}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={12} md={5.2} lg={6} xl={5} className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                <Grid container>
                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                            Current Age <Astrict />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GlobalTextfield
                                            variant="outlined"
                                            placeholder='Enter Age'
                                            size="medium"
                                            fullWidth
                                            autoComplete='off'
                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                            onKeyPress={(e: any) => {
                                                if (!/\d$/.test(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            onChange={(e) => handleInputChange('user_current_age', e.target.value)}
                                            value={formData.user_current_age}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.2} lg={6} xl={5} className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                <Grid container>
                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                            Current Account Balance <Astrict />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GlobalTextfield
                                            variant="outlined"
                                            placeholder=''
                                            size="medium"
                                            fullWidth
                                            autoComplete='off'
                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                            onKeyPress={(e: any) => { if (!/\d$/.test(e.key)) { e.preventDefault(); } }}
                                            InputProps={{ startAdornment: (<InputAdornment position="start"><IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" /></InputAdornment>) }}
                                            onChange={(e) => handleInputChange('user_tpi_curr_account_balance', e.target.value)}
                                            value={formData.user_tpi_curr_account_balance}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.2} lg={6} xl={5} className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                <Grid container>
                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                            Percent Fees (%) <Astrict />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GlobalTextfield
                                            variant="outlined"
                                            placeholder=''
                                            size="medium"
                                            fullWidth
                                            autoComplete='off'
                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                            onKeyPress={(e: any) => { const v = e.target.value; if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && v.includes('.'))) { e.preventDefault(); } }}
                                            InputProps={{ startAdornment: (<InputAdornment position="start"><IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" /></InputAdornment>) }}
                                            onChange={(e) => handleInputChange('user_tpi_percent_fees', e.target.value)}
                                            value={formData.user_tpi_percent_fees}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.2} lg={6} xl={5} className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                <Grid container>
                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                            Employer Match (Annual $ Contribution) <Astrict />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GlobalTextfield
                                            variant="outlined"
                                            placeholder=''
                                            size="medium"
                                            fullWidth
                                            autoComplete='off'
                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                            onKeyPress={(e: any) => { if (!/\d$/.test(e.key)) { e.preventDefault(); } }}
                                            InputProps={{ startAdornment: (<InputAdornment position="start"><IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" /></InputAdornment>) }}
                                            onChange={(e) => handleInputChange('user_tpi_employer_match', e.target.value)}
                                            value={formData.user_tpi_employer_match}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.2} lg={6} xl={5} className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                <Grid container>
                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                            Rate of Return during Retirement (%) <Astrict />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GlobalTextfield
                                            variant="outlined"
                                            placeholder=''
                                            size="medium"
                                            fullWidth
                                            autoComplete='off'
                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                            onKeyPress={(e: any) => { const v = e.target.value; if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && v.includes('.'))) { e.preventDefault(); } }}
                                            InputProps={{ startAdornment: (<InputAdornment position="start"><IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" /></InputAdornment>) }}
                                            onChange={(e) => handleInputChange('user_tpi_rate_of_return_dr', e.target.value)}
                                            value={formData.user_tpi_rate_of_return_dr}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.2} lg={6} xl={5} className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                <Grid container>
                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                            Rate of Return - Working Years (%) <Astrict />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GlobalTextfield
                                            variant="outlined"
                                            placeholder=''
                                            size="medium"
                                            fullWidth
                                            autoComplete='off'
                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                            onKeyPress={(e: any) => { const v = e.target.value; if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && v.includes('.'))) { e.preventDefault(); } }}
                                            InputProps={{ startAdornment: (<InputAdornment position="start"><IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" /></InputAdornment>) }}
                                            onChange={(e) => handleInputChange('user_tpi_rate_of_return_br', e.target.value)}
                                            value={formData.user_tpi_rate_of_return_br}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.2} lg={6} xl={5} className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                <Grid container>
                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                        <Grid container justifyContent={"space-between"} alignItems={"center"}>
                                            <Grid item>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Annual Spend <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Grid container alignItems={"center"} gap={"15px"}>
                                                    <Grid item>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            %
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <FormGroup
                                                            className={`${styles.agentStatusSwitch}`}
                                                            sx={{
                                                                "& .MuiFormControlLabel-root": {
                                                                    marginRight: "0px !important"
                                                                },
                                                            }}>
                                                            <FormControlLabel
                                                                label=""
                                                                sx={{ cursor: "pointer" }}
                                                                control={
                                                                    <IOSSwitch
                                                                        checked={formData?.annual_spend_percent_flag}
                                                                        sx={{
                                                                            marginRight: "0px !important"
                                                                        }}
                                                                        onChange={(e: any) => handleInputChange('annual_spend_percent_flag', e.target.checked)}
                                                                    />
                                                                }
                                                            />
                                                        </FormGroup>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GlobalTextfield
                                            variant="outlined"
                                            placeholder=''
                                            size="medium"
                                            fullWidth
                                            autoComplete='off'
                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                            onKeyPress={(e: any) => { if (!/\d$/.test(e.key)) { e.preventDefault(); } }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        {formData?.annual_spend_percent_flag ? (
                                                            <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                        ) : (
                                                            <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                        )}
                                                    </InputAdornment>
                                                )
                                            }}
                                            onChange={(e) => handleInputChange('annual_spend', e.target.value)}
                                            value={formData.annual_spend}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.2} lg={6} xl={5} className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                <Grid container>
                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                            Income Tax Rate (During Retirement) <Astrict />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GlobalTextfield
                                            variant="outlined"
                                            placeholder=''
                                            size="medium"
                                            fullWidth
                                            autoComplete='off'
                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                            onKeyPress={(e: any) => { const v = e.target.value; if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && v.includes('.'))) { e.preventDefault(); } }}
                                            InputProps={{ startAdornment: (<InputAdornment position="start"><IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" /></InputAdornment>) }}
                                            onChange={(e) => handleInputChange('user_tpi_it_rate_dr', e.target.value)}
                                            value={formData.user_tpi_it_rate_dr}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container gap={1}>
                                    <Grid item xs={12} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                        Payment Frequency <Astrict />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <RadioGroup
                                            row
                                            aria-labelledby="demo-radio-buttons-group-label"
                                            name="radio-buttons-group"
                                            onChange={handleRadioChange}
                                            sx={{
                                                "& .MuiButtonBase-root": {
                                                    padding: "9px 4px 9px 9px !important"
                                                }
                                            }}
                                        >
                                            <FormControlLabel
                                                value="monthly"
                                                control={<Radio
                                                    sx={{
                                                        color: "#3B78CE",
                                                        '&.Mui-checked': {
                                                            color: "#3B78CE",
                                                        },
                                                        "& .MuiFormControlLabel-root": { color: '#3B78CE' },
                                                    }}
                                                />}
                                                sx={{
                                                    color: '#314158',
                                                    "& .MuiTypography-root": {
                                                        fontWeight: '600 !important'
                                                    }
                                                }}
                                                label="Monthly"
                                                checked={!formData.user_tpi_annually_flag}
                                            />

                                            <FormControlLabel
                                                value="annually"
                                                control={<Radio
                                                    sx={{
                                                        color: "#3B78CE",
                                                        '&.Mui-checked': {
                                                            color: "#3B78CE",
                                                        },
                                                        "& .MuiFormControlLabel-root": { color: '#3B78CE' },
                                                    }}
                                                />}
                                                sx={{
                                                    color: '#314158',
                                                    "& .MuiTypography-root": {
                                                        fontWeight: '600 !important'
                                                    }
                                                }}
                                                label="Annually"
                                                checked={formData.user_tpi_annually_flag}
                                            />
                                        </RadioGroup>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Contribution Section */}
                            <Grid item xs={12}>
                                {formData.user_tpi_contribution_data.map((row: any, index: number) => (
                                    <Grid
                                        marginTop={index > 0 ? "20px" : "0px"}
                                        key={row.id}
                                        container
                                        alignItems={"flex-end"}
                                        gap={"12px"}
                                        sx={{ flexWrap: { xs: "wrap", sm: "nowrap" } }}
                                    >
                                        <Grid item xs={12} sm={12} md={5.2} lg={6} xl={5}>
                                            <Grid container>
                                                <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                    <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                        Contribution <Astrict />
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <GlobalTextfield
                                                        variant="outlined"
                                                        placeholder=''
                                                        size="medium"
                                                        fullWidth
                                                        autoComplete='off'
                                                        stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                        value={row.total_contribution}
                                                        onKeyPress={(e: any) => { if (!/\d$/.test(e.key)) { e.preventDefault(); } }}
                                                        onChange={(e) => handleContributionChange(index, 'total_contribution', e.target.value)}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={5.2} lg={6} xl={5}>
                                            <Grid container>
                                                <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                    <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                        Duration (Years) <Astrict />
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <GlobalTextfield
                                                        variant="outlined"
                                                        placeholder='Enter Years'
                                                        size="medium"
                                                        fullWidth
                                                        autoComplete='off'
                                                        value={row.duration}
                                                        onKeyPress={(e: any) => { if (!/\d$/.test(e.key)) { e.preventDefault(); } }}
                                                        onChange={(e) => handleContributionChange(index, 'duration', e.target.value)}
                                                        stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        {formData.user_tpi_contribution_data.length > 1 && (
                                            <Grid item xs={"auto"} sx={{ paddingBottom: "4px" }}>
                                                <IconButton onClick={() => removeContribution(index)}>
                                                    <CancelIcon sx={{ color: '#3B78CE' }} />
                                                </IconButton>
                                            </Grid>
                                        )}
                                    </Grid>
                                ))}
                                <Button
                                    fullWidth
                                    className={styles.newContributionButton}
                                    onClick={addContribution}
                                    startIcon={<IconsColor iconName="plusIcon" width="13px" height="13px" color="#3B78CE" />}
                                >
                                    New Contribution
                                </Button>
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    className={globalStyles.globalButtonStyleV1}
                                    onClick={buildPayload}
                                    disabled={!isFormValid()}
                                    sx={{
                                        width: { xs: "100%", sm: "300px" },
                                    }}
                                >
                                    Calculate
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ opacity: 0.8, borderColor: "#D4D4D4", display: { xs: "none", md: "block" } }}
                    />

                    {/* Right Side Section (Output Wrapper) */}
                    {retirementIncomeData && (
                        <Grid item xs={12} sm={12} md={12} lg={5.6} xl={5.8} className={styles.sideScrollPane}>
                            <Grid container gap={"20px"}>
                                {/* Section Title */}
                                <Grid item xs={12}>
                                    <Typography className={styles.retirementSummaryTitle}>
                                        Retirement Income Summary
                                    </Typography>
                                </Grid>

                                {/* Top Stats Bar */}
                                <Grid item xs={12}>
                                    <Grid container className={styles.summaryStatsBar}>
                                        <Grid item className={styles.statItem}>
                                            <Typography className={styles.statLabel}>Age at Retirement</Typography>
                                            <Typography className={styles.statValue}>
                                                {retirementIncomeData?.ageAtRetirement || 0}
                                            </Typography>
                                        </Grid>
                                        <Divider
                                            orientation="vertical"
                                            flexItem
                                            className={styles.statDivider}
                                            sx={{ display: { xs: "none", sm: "block" } }}
                                        />
                                        <Grid item className={styles.statItem}>
                                            <Typography className={styles.statLabel}>Tax Rate</Typography>
                                            <Typography className={styles.statValue}>
                                                {retirementIncomeData?.typical_plan?.tax_rate_during_retirement || "0"}%
                                            </Typography>
                                        </Grid>
                                        <Divider
                                            orientation="vertical"
                                            flexItem
                                            className={styles.statDivider}
                                            sx={{ display: { xs: "none", sm: "block" } }}
                                        />
                                        <Grid item className={styles.statItem}>
                                            <Typography className={styles.statLabel}>Annual Withdraw</Typography>
                                            <Typography className={styles.statValue}>
                                                {formatNumber(retirementIncomeData?.typical_plan?.annual_withdraw) || "$0"}
                                            </Typography>
                                        </Grid>
                                        <Divider
                                            orientation="vertical"
                                            flexItem
                                            className={styles.statDivider}
                                            sx={{ display: { xs: "none", sm: "block" } }}
                                        />
                                        <Grid item className={styles.statItem}>
                                            <Typography className={styles.statLabel}>Rate of Return</Typography>
                                            <Typography className={styles.statValue}>
                                                {retirementIncomeData?.typical_plan?.rateOfReturnRetirement || "0"}%
                                            </Typography>
                                        </Grid>
                                        <Divider
                                            orientation="vertical"
                                            flexItem
                                            className={styles.statDivider}
                                            sx={{ display: { xs: "none", sm: "block" } }}
                                        />
                                        <Grid item className={styles.statItem}>
                                            <Typography className={styles.statLabel}>Fees</Typography>
                                            <Typography className={styles.statValue}>
                                                {retirementIncomeData?.typical_plan?.percentFees || "0"}%
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Total Contributions Card */}
                                <Grid item xs={12}>
                                    <Grid container className={styles.totalContributionsCard} alignItems="center" gap={"16px"}>
                                        <Grid item className={styles.contributionIconWrapper}>
                                            <IconsColor iconName="dollarIcon" color="rgba(100,100,100,1)" width="22px" height="22px" />
                                        </Grid>
                                        <Grid item>
                                            <Typography className={styles.totalContributionsLabel}>
                                                Total Contributions to Account
                                            </Typography>
                                            <Typography className={styles.totalContributionsValue}>
                                                {formatNumber(retirementIncomeData?.typical_plan?.total_contribution) || "$0"}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Income During Retirement Header + Age stepper */}
                                <Grid item xs={12} sx={{ paddingRight: { xs: 0, sm: "24px" } }}>
                                    <Grid container justifyContent="space-between" alignItems="center" gap={"8px"}>
                                        <Grid item>
                                            <Typography className={styles.incomeSectionTitle}>
                                                Income During Retirement
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Grid container gap={"8px"} alignItems={"center"}>
                                                <Grid item className={styles.ageLabel}>Age:</Grid>
                                                <Grid item>
                                                    <CustomNumberTextField
                                                        type="number"
                                                        size="small"
                                                        variant="outlined"
                                                        value={changeYear}
                                                        onChange={(e) => {
                                                            setChangeYear(Number.parseInt(e?.target?.value) || null);
                                                        }}
                                                        InputProps={{ inputProps: { min: riAgeMin, max: riAgeMax } }}
                                                        onBlur={inputBlurChangeYear}
                                                        onKeyUp={(e) => {
                                                            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')
                                                                inputChangeYear(e);
                                                        }}
                                                        className={styles.ageInputField}
                                                        customStyles={{
                                                            backgroundColor: "white",
                                                            color: "black"
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* 4 Metric Cards */}
                                <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                        {/* Account Value */}
                                        <Grid item xs={12} sm={6}>
                                            <Grid container direction="column" gap={"8px"} className={`${styles.metricCard} ${styles.metricCardGreen}`}>
                                                <Grid item>
                                                    <Grid container alignItems="center" gap={"6px"}>
                                                        <Typography className={`${styles.metricCardLabel} ${styles.metricCardLabelGreen}`}>
                                                            Account Value
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                                <Grid item>
                                                    <Typography className={`${styles.metricCardValue} ${styles.metricCardValueGreen}`}>
                                                        {formatNumber(changeYear ? retirementIncomeData?.typical_plan?.RI_Age_data?.[changeYear]?.account_value : null) || "$0"}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        {/* Annual Spendable Income */}
                                        <Grid item xs={12} sm={6}>
                                            <Grid container direction="column" gap={"8px"} className={`${styles.metricCard} ${styles.metricCardBlue}`}>
                                                <Grid item>
                                                    <Grid container alignItems="center" gap={"6px"}>
                                                        <Typography className={`${styles.metricCardLabel} ${styles.metricCardLabelBlue}`}>
                                                            Annual Spendable Income
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                                <Grid item>
                                                    <Typography className={`${styles.metricCardValue} ${styles.metricCardValueBlue}`}>
                                                        {formatNumber(changeYear ? retirementIncomeData?.typical_plan?.RI_Age_data?.[changeYear]?.annual_sp_income : null) || "$0"}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        {/* Total Spendable Income */}
                                        <Grid item xs={12} sm={6}>
                                            <Grid container direction="column" gap={"8px"} className={`${styles.metricCard} ${styles.metricCardPurple}`}>
                                                <Grid item>
                                                    <Grid container alignItems="center" gap={"6px"}>
                                                        <Typography className={`${styles.metricCardLabel} ${styles.metricCardLabelPurple}`}>
                                                            Total Spendable Income
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                                <Grid item>
                                                    <Typography className={`${styles.metricCardValue} ${styles.metricCardValuePurple}`}>
                                                        {formatNumber(changeYear ? retirementIncomeData?.typical_plan?.RI_Age_data?.[changeYear]?.total_annual_sp_income : null) || "$0"}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        {/* Total Fees Paid */}
                                        <Grid item xs={12} sm={6}>
                                            <Grid container direction="column" gap={"8px"} className={`${styles.metricCard} ${styles.metricCardOrange}`}>
                                                <Grid item>
                                                    <Grid container alignItems="center" gap={"6px"}>
                                                        <Typography className={`${styles.metricCardLabel} ${styles.metricCardLabelOrange}`}>
                                                            Total Fees Paid
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                                <Grid item>
                                                    <Typography className={`${styles.metricCardValue} ${styles.metricCardValueOrange}`}>
                                                        {formatNumber(changeYear ? retirementIncomeData?.typical_plan?.FEES_age_data?.[changeYear]?.total_fees_paid : null) || "$0"}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Error Banner */}
                                {flagShowCalculationError &&
                                    <Grid item xs={12} className={styles.typicalValueEmptyTxt}>
                                        IRA account is empty
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                    )
                    }
                </Grid>
            </Grid>
        </Grid>
    )
}

export default SpendDownCalc